import { useCallback, useEffect, useRef } from 'react';
import { SearchProvider } from '../contexts/SearchProvider';
import shopify from '../../js/shopify';
import helpers from '../../js/helpers';
import api from '../../js/api';

/**
 * Debounce helper function to limit how often a function can be called
 * @param {Function} func - The function to debounce
 * @param {number} wait - The debounce delay in milliseconds
 * @return {Function} The debounced function
 */
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Creates a provider for Shopify search implementation
 * 
 * @param {Object} props - The props for the ShopifySearchProvider component
 * @param {React.ReactNode} props.children - Child components
 * @param {Object} props.settings - Settings for the Shopify search
 * @param {string} props.searchType - Type of search ('search', 'plp', 'header')
 * @returns {JSX.Element} A SearchProvider with Shopify-specific implementation
 */
export function ShopifySearchProvider({ children, settings, searchType = 'search' }) {
  // Create refs to maintain state across re-renders
  const providerRef = useRef({
    initialized: false,
    currentSort: 'RELEVANCE',
    currentDirection: 'DESC',
    currentFilters: [],
    currentPage: 1,
    currentCollectionSize: 0,
    currentCollection: 'all',
    lastCursor: null,
    currentResults: 0,
    searchQuery: '',
    productsPerPage: 24,
    isProcessingRequest: false,
    debouncedSearchRequest: null,
  });

  const provider = providerRef.current;

  // Initialize the provider
  const initialize = useCallback((searchSettings) => {
    if (provider.initialized) return;

    // Set default values
    provider.productsPerPage = searchSettings.products_per_page || 24;
    provider.currentSort = searchSettings?.default_sort || 'RELEVANCE';
    provider.initialized = true;

    // Create debounced search function with 300ms delay
    provider.debouncedSearchRequest = debounce(async (detail) => {
      const { query, type, collection, collectionSize, productsPerPage, isOtherCollection = false } = detail;
      if (provider.isProcessingRequest) return;

      provider.isProcessingRequest = true;
      provider.type = type;

      if (type === 'plp') {
        // Collection search
        provider.currentCollection = collection;
        provider.currentCollectionSize = collectionSize;

        await makeCollectionGraphRequest(isOtherCollection);
      } else if (type === 'header') {
        // Query search
        provider.searchQuery = query;
        await makeHeaderSearchGraphRequest();
      } else {
        // Query search
        provider.searchQuery = query;
        provider.currentCollectionSize = collectionSize;

        await makeSearchGraphRequest();
      }

      provider.isProcessingRequest = false;
    }, 300); // 300ms debounce delay

    // Set up event listeners
    window.addEventListener('searchrequest', handleSearchRequest);
    window.addEventListener('searchrequest-sort-applied', handleSortChange);
    window.addEventListener('searchrequest-filter-applied', handleFilterApplied);
    window.addEventListener('nextpage', handleNextPage);
  }, []);

  // Handle search requests
  const handleSearchRequest = useCallback((event) => {
    // Use the debounced search function instead of processing immediately
    if (provider.debouncedSearchRequest) {
      provider.debouncedSearchRequest(event.detail);
    }
  }, []);

  // Handle sort changes
  const handleSortChange = useCallback(async (event) => {
    const { sortOption } = event.detail;

    if (provider.isProcessingRequest) return;
    provider.isProcessingRequest = true;

    // Map sort options to Shopify GraphQL format
    let sort = 'MANUAL';
    let direction = 'ASC';

    switch (sortOption) {
      case 'Newest':
        sort = 'CREATED';
        break;
      case 'Popular':
        sort = 'BEST_SELLING';
        break;
      case 'LowToHigh':
        sort = 'PRICE';
        direction = 'ASC';
        break;
      case 'HighToLow':
        sort = 'PRICE';
        direction = 'DESC';
        break;
      case 'Featured':
        sort = 'MANUAL';
        direction = 'ASC';
        break;
    }

    provider.currentSort = sort;
    provider.currentDirection = direction;
    provider.currentPage = 1; // Reset to first page
    provider.lastCursor = null; // Reset cursor

    if (provider.searchQuery) {
      await makeSearchGraphRequest();
    } else {
      await makeCollectionGraphRequest();
    }

    provider.isProcessingRequest = false;
  }, []);

  // Handle filter changes
  const handleFilterApplied = useCallback(async (event) => {
    const { filters_object } = event.detail;

    if (provider.isProcessingRequest) return;
    provider.isProcessingRequest = true;

    // Convert filters_object to Shopify GraphQL filters
    let filters = [];
    if (filters_object) {
      Object.entries(filters_object).forEach(([key, value]) => {
        if (key === 'Room' && Array.isArray(value)) {
          value.forEach((room) => {
            filters.push({ tag: room });
          });
        }
        if (key === 'Category' && Array.isArray(value)) {
          value.forEach((category) => {
            filters.push({
              productMetafield: {
                namespace: 'shopify',
                key: 'category',
                value: category,
              }
            });
          });
        }
        if (key === 'Price' && Array.isArray(value)) {
          value.forEach((priceRange) => {
            const [min, max] = priceRange.split('-');
            filters.push({
              price: { min: parseFloat(min), max: parseFloat(max) },
            });
          });
        }
        if (key === 'Brand' && Array.isArray(value)) {
          value.forEach((brand) => {
            filters.push({ productVendor: brand });
          });
        }
        if (key === 'Product type' && Array.isArray(value)) {
          value.forEach((type) => {
            filters.push({ productType: type });
          });
        }
        if (key === 'Availability' && Array.isArray(value)) {
          value.forEach((availability) => {
            filters.push({
              available: availability === 'In stock',
            });
          });
        }
        if (key === 'Variant' && Array.isArray(value)) {
          value.forEach((variant) => {
            const [name, val] = variant.split(':');
            filters.push({
              variantOption: { name, value: val },
            });
          });
        }
      });
    }

    provider.currentFilters = filters;
    provider.currentPage = 1; // Reset to first page
    provider.lastCursor = null; // Reset cursor

    if (provider.searchQuery) {
      await makeSearchGraphRequest();
    } else {
      await makeCollectionGraphRequest();
    }

    provider.isProcessingRequest = false;
  }, []);

  // Handle pagination requests
  const handleNextPage = useCallback(async (event) => {
    const { page } = event.detail;

    if (provider.isProcessingRequest) return;
    provider.isProcessingRequest = true;

    provider.currentPage = page;

    if (provider.searchQuery) {
      await makeSearchGraphRequest();
    } else {
      await makeCollectionGraphRequest();
    }

    provider.isProcessingRequest = false;
  }, []);

  // Fetch products from collection with GraphQL
  const fetchProductsFromCollection = useCallback(async (
    handle,
    first,
    after = null,
    filters = [],
    sort = provider.currentSort,
    direction = provider.currentDirection
  ) => {
    window.dispatchEvent(
      new CustomEvent('show-spinner', {
        detail: {
          id: 'spinner-collection',
        },
      })
    );

    const endpoint = "/api/2024-01/graphql.json";
    const accessToken = api.getStorefrontToken();

    // GraphQL query for collections
    const query = `
      query GetCollection(
        $handle: String!
        $first: Int!
        $after: String
        $sort: ProductCollectionSortKeys
        $direction: Boolean
        $productFilters: [ProductFilter!]
      ) {
        collectionByHandle(handle: $handle) {
          products(
            first: $first
            after: $after
            sortKey: $sort
            reverse: $direction
            filters: $productFilters
          ) {
            edges {
              cursor
              node {
                id
                title
                handle
                tags
                productType
                createdAt
                totalInventory
                vendor
                options {
                  name
                  values
                }
                featuredImage {
                  altText
                  url
                }
                isGiftCard
                images(first: 10) {
                  edges {
                    node {
                      altText
                      url
                    }
                  }
                }

                product_bundle: metafield(namespace: "custom", key: "product_bundle") {
                  references(first: 1) {
                    edges {
                      node {
                        ... on Metaobject {
                          fields {
                            key
                            type
                            value
                          }
                        }
                      }
                    }
                  }
                }

                metafields(
                  identifiers: [
                    { namespace: "custom", key: "product_bundle" }
                    { namespace: "custom", key: "best-value" }
                    { namespace: "custom", key: "product-z" }
                    { namespace: "custom", key: "product_dosage_klaviyo_form" }
                    { namespace: "custom", key: "product_dosage" }
                    { namespace: "custom", key: "product_related_discount_codes" }
                  ]
                ) {
                  namespace
                  key
                  value
                  type
                  description
                }
                dosage_form: metafield(namespace: "custom", key: "product_dosage_klaviyo_form") {
                    value
                }

                sellingPlanGroups(first: 5) {
                  edges {
                    node {
                      name
                      options {
                        name
                        values
                      }
                      sellingPlans(first: 5) {
                        edges {
                          node {
                            id
                            name
                            description
                            priceAdjustments {
                              adjustmentValue {
                                ... on SellingPlanFixedPriceAdjustment {
                                  price {
                                    amount
                                    currencyCode
                                  }
                                }
                                ... on SellingPlanPercentagePriceAdjustment {
                                  adjustmentPercentage
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
                priceRange {
                  maxVariantPrice {
                    amount
                  }
                  minVariantPrice {
                    amount
                  }
                }
                variants(first: 100) {
                  edges {
                    node {
                      id
                      availableForSale
                      sku
                      selectedOptions {
                        value
                      }
                      compareAtPrice {
                        amount
                      }
                      image {
                        altText
                        url
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    // Convert direction to boolean: true for ASC, false for DESC
    const directionBoolean = direction === "ASC";

    const variables = {
      handle: handle,
      first: first,
      after: after, // Use the cursor for pagination
      sort: sort,
      direction: directionBoolean,
      productFilters: filters,
    };

    // Execute the query
    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": accessToken,
        },
        body: JSON.stringify({
          query: query,
          variables: variables,
        }),
      });

      if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.statusText}`);
      }

      const result = await response.json();
      return result?.data?.collectionByHandle?.products;
    } catch (error) {
      console.error('Error fetching products from collection:', error);
      return null;
    }
  }, []);

  // Fetch products from search with GraphQL
  const fetchProductsFromSearch = useCallback(async (
    searchTerm,
    first,
    after = null,
    filters = [],
    sort = "RELEVANCE",
    direction = "ASC"
  ) => {

    window.dispatchEvent(
      new CustomEvent('show-spinner', {
        detail: {
          id: 'spinner-collection',
        },
      })
    );

    const endpoint = "/api/2024-01/graphql.json";
    const accessToken = api.getStorefrontToken();

    // GraphQL query for search
    const query = `
      query SearchProducts(
        $query: String!
        $first: Int!
        $after: String
        $sort: SearchSortKeys
        $direction: Boolean
        $productFilters: [ProductFilter!]
      ) {
        search(
          query: $query
          first: $first
          after: $after
          sortKey: $sort
          reverse: $direction
          types: [PRODUCT,ARTICLE]
          productFilters: $productFilters
        ) {
          edges {
            cursor
            node {
              ... on Article {
                id
                handle
                excerpt
                excerptHtml
                title
                onlineStoreUrl
                tags
                blog {
                  handle
                  title
                }
                author {
                  name
                }
                image {
                  src
                }
              }
              ... on Product {
                id
                title
                handle
                tags
                productType
                createdAt
                totalInventory
                vendor
                options {
                  name
                  values
                }
                featuredImage {
                  altText
                  url
                }
                isGiftCard
                images(first: 10) {
                  edges {
                    node {
                      altText
                      url
                    }
                  }
                }
                product_bundle: metafield(namespace: "custom", key: "product_bundle") {
                  references(first: 1) {
                      edges {
                      node {
                          ... on Metaobject {
                          fields {
                              key
                              type
                              value
                          }
                          }
                      }
                      }
                  }
                }

                metafields(
                  identifiers: [
                    { namespace: "custom", key: "product_bundle" }
                    { namespace: "custom", key: "best-value" }
                    { namespace: "custom", key: "product-z" }
                    { namespace: "custom", key: "product_dosage_klaviyo_form" }
                    { namespace: "custom", key: "product_dosage" }
                    { namespace: "custom", key: "product_related_discount_codes" }
                  ]
                ) {
                  namespace
                  key
                  value
                  type
                  description
                }

                sellingPlanGroups(first: 5) {
                  edges {
                    node {
                      name
                      options {
                        name
                        values
                      }
                      sellingPlans(first: 5) {
                        edges {
                          node {
                            id
                            name
                            description
                            priceAdjustments {
                              adjustmentValue {
                                ... on SellingPlanFixedPriceAdjustment {
                                  price {
                                    amount
                                    currencyCode
                                  }
                                }
                                ... on SellingPlanPercentagePriceAdjustment {
                                  adjustmentPercentage
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
                priceRange {
                  maxVariantPrice {
                    amount
                  }
                  minVariantPrice {
                    amount
                  }
                }
                variants(first: 100) {
                  edges {
                    node {
                      id
                      availableForSale
                      sku
                      selectedOptions {
                        value
                      }
                      compareAtPrice {
                        amount
                      }
                      image {
                        altText
                        url
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    // Convert direction to boolean: true for ASC, false for DESC
    const directionBoolean = direction === "ASC";

    const variables = {
      query: searchTerm,
      first: first,
      after: after, // Use the cursor for pagination
      sort: sort,
      direction: directionBoolean,
      productFilters: filters,
    };

    // Execute the query
    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": accessToken,
        },
        body: JSON.stringify({
          query: query,
          variables: variables,
        }),
      });

      if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.statusText}`);
      }

      const result = await response.json();

      return result.data.search.edges;
    } catch (error) {
      console.error('Error fetching products from search:', error);
      return null;
    }
  }, []);

  // Fetch products from search with GraphQL
  const fetchProductsFromHeaderSearch = useCallback(async (searchTerm) => {
    const endpoint = "/api/2024-10/graphql.json";
    const accessToken = api.getStorefrontToken();

    // GraphQL query for search
    const query = `
      query SearchProducts(
        $query: String!
      ) {
        predictiveSearch  (
          query: $query
          types: [PRODUCT,ARTICLE]
          limitScope: ALL
          searchableFields: [AUTHOR,BODY,TITLE,TAG]
        ) {
          articles {
            id
            handle
            excerpt
            excerptHtml
            title
            onlineStoreUrl
            tags
            blog {
              handle
              title
            }
            author {
              name
            }
            image {
              src
            }
          }
          queries {
            text
            styledText
          }
          products {
            id
            title
            handle
            tags
            productType
            createdAt
            totalInventory
            vendor
            options {
              name
              values
            }
            featuredImage {
              altText
              url
            }
            isGiftCard
            images(first: 10) {
              edges {
                node {
                  altText
                  url
                }
              }
            }
            product_bundle: metafield(namespace: "custom", key: "product_bundle") {
              references(first: 1) {
                  edges {
                  node {
                      ... on Metaobject {
                      fields {
                          key
                          type
                          value
                      }
                      }
                  }
                  }
              }
            }

            metafields(
              identifiers: [
                { namespace: "custom", key: "product_bundle" }
                { namespace: "custom", key: "best-value" }
                { namespace: "custom", key: "product-z" }
                { namespace: "custom", key: "product_dosage_klaviyo_form" }
                { namespace: "custom", key: "product_dosage" }
                { namespace: "custom", key: "product_related_discount_codes" }
              ]
            ) {
              namespace
              key
              value
              type
              description
            }
            sellingPlanGroups(first: 5) {
              edges {
                node {
                  name
                  options {
                    name
                    values
                  }
                  sellingPlans(first: 5) {
                    edges {
                      node {
                        id
                        name
                        description
                        priceAdjustments {
                          adjustmentValue {
                            ... on SellingPlanFixedPriceAdjustment {
                              price {
                                amount
                                currencyCode
                              }
                            }
                            ... on SellingPlanPercentagePriceAdjustment {
                              adjustmentPercentage
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
            priceRange {
              maxVariantPrice {
                amount
              }
              minVariantPrice {
                amount
              }
            }
            variants(first: 100) {
              edges {
                node {
                  id
                  availableForSale
                  sku
                  selectedOptions {
                    value
                  }
                  compareAtPrice {
                    amount
                  }
                  image {
                    altText
                    url
                  }
                }
              }
            }
          }
        }
      }
    `;

    const variables = {
      query: searchTerm,
    };

    // Execute the query
    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": accessToken,
        },
        body: JSON.stringify({
          query: query,
          variables: variables,
        }),
      });

      if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching products from search:', error);
      return null;
    }
  }, []);

  // Process collection results and dispatch events
  const makeCollectionGraphRequest = useCallback(async (isOtherCollection = false) => {
    const productsData = await fetchProductsFromCollection(
      provider.currentCollection,
      provider.productsPerPage,
      isOtherCollection? null : provider.lastCursor,
      provider.currentFilters,
      provider.currentSort,
      provider.currentDirection
    );

 

    if (!productsData) return;

    // Extract product nodes and the last cursor
    const productNodes = productsData.edges?.map((edge) =>
      shopify.transformGraphqlProduct(edge.node)
    );


    provider.lastCursor =
      productsData.edges?.length > 0 && !isOtherCollection
        ? productsData.edges[productsData.edges.length - 1].cursor
        : null;

    // Adjust the collection size if filters are applied
    let paginationTotal = provider.currentCollectionSize;
    if (provider.currentFilters.length > 0) {
      paginationTotal = productNodes.length;
    }

    // Extract facets from product data
    const facets = extractFacetsFromProducts(productNodes);

    // Dispatch filter list event
    window.dispatchEvent(
      new CustomEvent("filter-list", {
        detail: {
          type: "plp",
          filters: facets,
        },
      })
    );

    // Dispatch product list event
    window.dispatchEvent(
      new CustomEvent("product-list", {
        detail: {
          type: "plp",
          page: provider.currentPage,
          products: productNodes,
          totalResults: paginationTotal,
          handle: provider?.currentCollection

        },
      })
    );
  }, [fetchProductsFromCollection]);

  // Process search results and dispatch events
  const makeSearchGraphRequest = useCallback(async () => {
    const productsData = await fetchProductsFromSearch(
      provider.searchQuery,
      provider.productsPerPage,
      provider.lastCursor,
      provider.currentFilters,
      provider.currentSort === 'MANUAL' ? 'RELEVANCE' : provider.currentSort,
      provider.currentDirection
    );

    if (!productsData) return;

    // Extract product nodes and the last cursor
    const productNodes = productsData
      .filter((product) => product.node.id.includes("Product"))
      .map((product) => shopify.transformGraphqlProduct(product.node));
    // Extract product nodes and the last cursor

    const articles = productsData
      .filter((article) => article.node.id.includes("Article"))
      .map(article => article.node);

    provider.lastCursor =
      productsData.length > 0
        ? productsData[productsData.length - 1].cursor
        : null;

    // Adjust for search results
    let paginationTotal = provider.currentCollectionSize || null;

    if (provider.currentFilters.length > 0) {
      paginationTotal = productNodes.length;
    }

    if (paginationTotal === 0 || !paginationTotal) {
      // If no filters are applied, set pagination total to the number of products returned
      paginationTotal = productNodes.length;
    }

    // Extract facets from product data
    const facets = extractFacetsFromProducts(productNodes);

    // Dispatch filter list event
    window.dispatchEvent(
      new CustomEvent("filter-list", {
        detail: {
          type: "search",
          filters: facets,
        },
      })
    );

    provider.isProcessingRequest = false;
    // Dispatch product list event
    window.dispatchEvent(
      new CustomEvent("searchrequest-products-complete", {
        detail: {
          query: provider.searchQuery,
          type: provider.type,
          page: provider.currentPage,
          products: productNodes,
          articles,
          totalResults: paginationTotal,
          filters: facets,
        },
      })
    );
  }, []);

  // Process header search results and dispatch events
  const makeHeaderSearchGraphRequest = useCallback(async () => {
    // const productsData = null;
    const results = await fetchProductsFromHeaderSearch(provider.searchQuery);

    const { products, articles, queries } = results?.data?.predictiveSearch || {};

    const productNodes = products.map((product) => shopify.transformGraphqlProduct(product));

    provider.isProcessingRequest = false;
    // Dispatch product list event
    window.dispatchEvent(
      new CustomEvent("header-search-complete", {
        detail: {
          query: provider.searchQuery,
          type: provider.type,
          products: productNodes,
          articles,
          queries
        },
      })
    );
  }, []);

  // Helper function to extract facets from product data
  const extractFacetsFromProducts = useCallback((products) => {
    if (!products || !Array.isArray(products)) return [];

    const facets = [];

    // Extract product types
    const productTypes = [...new Set(products.map(p => p.product_type))].filter(Boolean);
    if (productTypes.length) {
      facets.push({
        key: 'Product type',
        label: 'Product Type',
        values: productTypes.map(type => ({
          key: type,
          label: type,
          count: products.filter(p => p.product_type === type).length
        }))
      });
    }

    // Extract vendors
    const vendors = [...new Set(products.map(p => p.vendor))].filter(Boolean);
    if (vendors.length) {
      facets.push({
        key: 'Brand',
        label: 'Brand',
        values: vendors.map(vendor => ({
          key: vendor,
          label: vendor,
          count: products.filter(p => p.vendor === vendor).length
        }))
      });
    }

    // Extract tags
    const allTags = products.flatMap(p => p.tags || []);
    const tags = [...new Set(allTags)].filter(Boolean);
    if (tags.length) {
      facets.push({
        key: 'Room',
        label: 'Room',
        values: tags.map(tag => ({
          key: tag,
          label: tag,
          count: allTags.filter(t => t === tag).length
        }))
      });
    }

    // Add availability facet
    facets.push({
      key: 'Availability',
      label: 'Availability',
      values: [
        {
          key: 'In stock',
          label: 'In stock',
          count: products.filter(p => p.available).length
        },
        {
          key: 'Out of stock',
          label: 'Out of stock',
          count: products.filter(p => !p.available).length
        }
      ]
    });

    return facets;
  }, []);

  // Create the provider object that our base SearchProvider will use
  const shopifyProvider = {
    initialize,
    handleSearchRequest,
    handleSortChange,
    handleFilterApplied,
    handleNextPage,
  };

  return (
    <SearchProvider provider={shopifyProvider} settings={settings} searchType={searchType}>
      {children}
    </SearchProvider>
  );
}

export default ShopifySearchProvider;